import { useMemo, useSyncExternalStore } from "react";

/**
 * Who is visiting, remembered in the browser so Home can open on their own
 * team: a manager, or a spectator who said they're just watching. A
 * convenience only: nothing depends on it, and storage can be missing or throw
 * (private windows, blocked site data), in which case the choice lasts until
 * the page is closed.
 */
export type Viewer = { kind: "manager"; personId: string } | { kind: "spectator" } | null;

/** The key predates spectators and holds a person id, so existing picks still work. */
const STORAGE_KEY = "ajff:my-manager";
/** Stored in place of a person id. Person ids are slugs of a name, so none can start with "~". */
const SPECTATOR = "~spectator";

const listeners = new Set<() => void>();
/** The choice when storage can't hold it. */
let inMemory: string | null = null;
/** Set once a write to storage fails; from then on the in-memory choice stands in for it. */
let storageFailed = false;

function read(): string | null {
  if (storageFailed) return inMemory;
  try {
    // Storage is the truth while it works, so a choice cleared in another tab clears here too.
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return inMemory;
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // A choice made (or cleared) in another tab. That tab's write proves storage works, so it is the truth again here,
  // even if a write from this tab failed earlier and left us on the in-memory copy.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    storageFailed = false;
    inMemory = event.newValue;
    listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

function toViewer(stored: string | null): Viewer {
  if (stored === null) return null;
  return stored === SPECTATOR ? { kind: "spectator" } : { kind: "manager", personId: stored };
}

function toStored(viewer: Viewer): string | null {
  if (viewer === null) return null;
  return viewer.kind === "spectator" ? SPECTATOR : viewer.personId;
}

function setViewer(viewer: Viewer): void {
  const stored = toStored(viewer);
  inMemory = stored;
  try {
    if (stored === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, stored);
    storageFailed = false;
  } catch {
    // Kept in memory above.
    storageFailed = true;
  }
  listeners.forEach((listener) => listener());
}

/** Who the visitor said they are (null: not asked yet, or forgotten) and a setter; null forgets it. */
export function useViewer(): readonly [Viewer, (viewer: Viewer) => void] {
  const stored = useSyncExternalStore(subscribe, read, () => null);
  // Derived per stored value so the object only changes when the choice does.
  const viewer = useMemo(() => toViewer(stored), [stored]);
  return [viewer, setViewer] as const;
}

/** The remembered `HistoryPerson.id` (or null, also for a spectator) and a setter; null forgets it. */
export function useMyManager(): readonly [string | null, (personId: string | null) => void] {
  const [viewer, set] = useViewer();
  const setManager = (personId: string | null) => set(personId === null ? null : { kind: "manager", personId });
  return [viewer?.kind === "manager" ? viewer.personId : null, setManager] as const;
}
